import { useState } from "react";
import { Eye } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

// Renders an ISSUED revision snapshot exactly as issued (never live booking fields). Shared by the client workspace, staff and the restricted link.
export function IssuedConfirmationView({ data, testId = "issued-confirmation" }) {
  return (
    <article data-testid={testId} className="space-y-4 rounded-[10px] border border-jigsaw-border bg-white p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-jigsaw-muted">Accommodation confirmation</p>
          <h2 className="font-mono-ref text-[22px] font-semibold text-jigsaw-navy" data-testid={`${testId}-reference`}>{data.booking_reference}</h2>
          <p className="text-sm text-jigsaw-muted">{data.client_name} · Revision {data.revision_number} · issued {data.issued_at ? new Date(data.issued_at).toLocaleString("en-GB") : "—"}</p>
        </div>
        <div className="text-right"><span data-testid={`${testId}-status`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${data.status === "CURRENT" ? "border-jigsaw-success/40 bg-jigsaw-successBg text-jigsaw-success" : "border-jigsaw-warning/40 bg-jigsaw-warningBg text-jigsaw-warning"}`}>{data.status === "CURRENT" ? "Current" : "Superseded"}</span><p className="mt-1 max-w-[18rem] text-[12px] text-jigsaw-muted" data-testid={`${testId}-status-detail`}>{data.detail}</p></div>
      </header>
      {data.status !== "CURRENT" && <p data-testid={`${testId}-superseded-notice`} className="rounded-md border border-jigsaw-warning/30 bg-jigsaw-warningBg px-3 py-2 text-[13px] text-jigsaw-warning">This revision has been superseded{data.superseded_by ? ` by revision ${data.superseded_by.revision_number}` : ""}. It is shown as it was issued and no longer describes the current booking.</p>}
      <ul className="divide-y divide-jigsaw-border rounded-md border border-jigsaw-border" data-testid={`${testId}-stays`}>
        {data.stays.length === 0 && <li className="px-3 py-2 text-sm text-jigsaw-muted">No stays were recorded on this revision.</li>}
        {data.stays.map((s, i) => (
          <li key={i} className="grid grid-cols-1 gap-1 px-3 py-2 text-sm sm:grid-cols-3" data-testid={`${testId}-stay-${i}`}>
            <span className="font-medium text-jigsaw-navy">{s.property_name || "Property"}</span>
            <span className="font-mono-ref text-[13px] text-jigsaw-navy">{fmt(s.check_in_date)} → {fmt(s.check_out_date)}</span>
            <span className="text-jigsaw-muted sm:text-right">{s.room_count ?? "—"} room{s.room_count === 1 ? "" : "s"}</span>
          </li>
        ))}
      </ul>
      <p className="text-[12px] text-jigsaw-muted">The authoritative confirmation is held in Atlas; this page shows the issued revision, not a copy that can drift.</p>
    </article>
  );
}

// Signed-in users (client or staff): fetches the issued revision through the booking's own authorisation. No download token is involved.
export function ViewOnlineButton({ bookingId, revision, testId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const show = async () => {
    try { const r = await api.get(`/bookings/${bookingId}/confirmation/revisions/${revision.id}/view`); setData(r.data); setOpen(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open the confirmation"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" data-testid={testId} onClick={show}><Eye className="mr-1 h-3.5 w-3.5" /> View online</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>Confirmation — revision {revision.revision_number}</DialogTitle><DialogDescription>Shown exactly as issued.</DialogDescription></DialogHeader>
          {data && <IssuedConfirmationView data={data} testId={`${testId}-view`} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
